import React from 'react'
import { ProtectRulesDefaultsGridView } from './protect-rules-defaults-grid-view'
import {
  ParentRowData,
  SortConfig,
  SortOrder
} from '../../legacy-design-system/legacy-grid'
import { useProtectRulesSummary, useProtectRulesQuickFilters } from '.'
import { ProtectRule, ProtectRuleWithParent } from './shared'
import { useAgentFeatures } from '../agent-features'
import { ViewerLoader } from '../viewer'
import debounce from 'lodash.debounce'
import { SortName } from '../../api/queries/protect-rules'

interface ProtectRulesDefaultsManagerProps {
  readonly organizationUuid: string
}

export function ProtectRulesDefaultsManager({
  organizationUuid
}: ProtectRulesDefaultsManagerProps) {
  const [filterText, setFilterText] = React.useState<string | undefined>(
    undefined
  )
  const [quickFilterId, setQuickFilterId] = React.useState<string>('ALL')
  const [sortConfig, setSortConfig] = React.useState<
    SortConfig<ProtectRuleWithParent>
  >({ order: SortOrder.ASC, field: 'name' })
  const {
    isLoading: agentFeaturesLoading,
    data: agentFeatures,
    error: agentFeaturesError
  } = useAgentFeatures()
  const { data, isLoading, error } = useProtectRulesSummary(organizationUuid, {
    q: filterText,
    quickFilter: quickFilterId,
    orderBy: {
      direction: sortConfig.order,
      field: sortConfig.field as SortName
    }
  })
  const {
    data: quickFilters,
    isLoading: quickFiltersLoading,
    error: quickFiltersError
  } = useProtectRulesQuickFilters(organizationUuid, { q: filterText })

  if (error || agentFeaturesError || quickFiltersError) {
    const message = getErrorMessage({
      error,
      agentFeaturesError,
      quickFiltersError
    })
    throw new Error(message)
  }

  const rules = data ? data : []
  const normalizedRules: ProtectRuleWithParent[] = rules.map(
    normalizeProtectRuleToWithParent
  )

  const onFilterChange = ({ filterType }) => {
    setQuickFilterId(filterType)
  }

  const onSearch = debounce((text) => {
    setFilterText(text)
  }, 500)

  /* TODO - TS-9606 - replace ViewerLoader usage with ReactQuery */
  return (
    <ViewerLoader>
      {({ viewer, loading: viewerLoading }) => {
        if (viewerLoading || !viewer) {
          return false
        }

        const {
          currentOrganization: { role }
        } = viewer

        const uniqueRowDataParents = normalizedRules
          .map((rule) => rule.parent)
          .reduce<ParentRowData[]>((acc, curr) => {
            return acc.find((row) => row.id === curr.id) ? acc : [...acc, curr]
          }, [])

        return (
          <ProtectRulesDefaultsGridView
            organizationUuid={organizationUuid}
            organizationRole={role}
            loading={isLoading || agentFeaturesLoading || quickFiltersLoading}
            rowData={sortRules(normalizedRules, sortConfig.order)}
            rowDataParents={uniqueRowDataParents}
            onSort={setSortConfig}
            onFilterChange={onFilterChange}
            onSearch={onSearch}
            activeSortConfigs={[sortConfig]}
            agentFeatures={agentFeatures}
            selectedQuickFilter={quickFilterId}
            quickFilters={quickFilters || []}
          />
        )
      }}
    </ViewerLoader>
  )
}

function normalizeProtectRuleToWithParent({
  ruleId,
  name,
  uuid,
  description,
  development,
  qa,
  production,
  languages,
  canBlockAtPerimeter,
  isMonitorAtPerimeter,
  canBlock,
  parentRuleName,
  parentRuleUuid
}: ProtectRule): ProtectRuleWithParent {
  return {
    id: ruleId.toString(),
    name,
    uuid,
    description,
    development,
    qa,
    production,
    languages,
    canBlockAtPerimeter,
    isMonitorAtPerimeter,
    canBlock,
    parent: {
      id: parentRuleUuid,
      name: parentRuleName
    }
  }
}

export function getErrorMessage(errors: {
  readonly [key: string]: Error | null
}): string {
  for (const key in errors) {
    if (errors.hasOwnProperty(key)) {
      const error = errors[key]
      if (!!error && error.message) {
        return error.message
      }
    }
  }
  return 'An Error Occurred'
}

export function getRuleNameOrChildRuleName(ruleName: string) {
  const ruleSplit = ruleName.split('-').map((rule) => rule.trim())
  return ruleSplit.length === 1 ? ruleSplit[0] : ruleSplit[1]
}

export function sortRules(
  rules: ProtectRuleWithParent[],
  order: SortOrder
): ProtectRuleWithParent[] {
  return rules.slice().sort((a, b) => {
    const aName = getRuleNameOrChildRuleName(a.name)
    const bName = getRuleNameOrChildRuleName(b.name)

    return order === 'ASC'
      ? aName.localeCompare(bName)
      : bName.localeCompare(aName)
  })
}
